import type AdNetworkSignalEvent from "app/pages/sales-intelligence/sub-modules/signals/models/ad-network/AdNetworkSignalEvent";
import SignalType from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalType";
import type { EcommerceSignalMetricChangeDirection } from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/EcommerceSignalMetricChangeDirection";
import type { NewsCategory } from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsCategory";
import type TechnologyChangesType from "app/pages/sales-intelligence/sub-modules/signals/models/technology/TechnologyChangesType";
import type TechnologySignalSubType from "app/pages/sales-intelligence/sub-modules/signals/models/technology/TechnologySignalSubType";
import type TrafficChangesModel from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficChangesModel";
import type TrafficMarketModel from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficMarketModel";

export type SignalEventsRequestParams = {
    domain: string;
    take?: number;
};

type BaseSignalEventDto = {
    eventHappenedDaysAgo: number;
    /** Present on account-overview API payloads when splitting primary vs other-domain events */
    isOther?: boolean;
    type: SignalType;
};

export type NewsSignalEventDto = BaseSignalEventDto & {
    url: string;
    articleSentence: string;
    title: string;
    categories: NewsCategory[];
    jobTitle?: string;
    location?: string;
    relatedCompanyDomain?: string;
    product?: string;
    financingType?: string;
    amount?: string;
    type: SignalType.News;
};

export type TechnologySignalEventDto = BaseSignalEventDto & {
    changeType: TechnologyChangesType;
    technologies: string[];
    renewalDate?: string;
    periodDays?: number;
    type: SignalType.Technology;
    subType?: TechnologySignalSubType;
};

export type AdNetworkSignalEventDto = BaseSignalEventDto & {
    changeType: AdNetworkSignalEvent;
    adNetworkIds: number[];
    adNetworkNames: string[];
    type: SignalType.AdNetwork;
};

export type TrafficSignalEventDto = BaseSignalEventDto & {
    type: SignalType.Traffic;
    filters: {
        trafficChanges?: TrafficChangesModel;
        newMarkets?: TrafficMarketModel;
    };
    countries?: readonly number[];
};

export type EcommerceSignalChangeFilterDto = {
    metric: string;
    direction: string;
    period: string;
};

export type EcommerceSignalBucketTransitionFilterDto = {
    metric: string;
    bucket: string;
};

export type EcommerceSignalFiltersDto = {
    change: EcommerceSignalChangeFilterDto | null;
    bucketTransition: EcommerceSignalBucketTransitionFilterDto | null;
    /** Set on bucket-transition events when `change` is null */
    direction?: string;
};

export type EcommerceSignalEventDto = BaseSignalEventDto & {
    type: SignalType.Ecommerce;
    subType: string;
    filters: EcommerceSignalFiltersDto;
    metricChange: number | null;
    previousMetricValue: string | null;
    newMetricValue: string | null;
    direction?: EcommerceSignalMetricChangeDirection;
};

export type IntentCountryDto = {
    country: string;
    city: string;
    countryCode: number;
    compositeScore: number;
    compositeScoreDelta: number;
};

export type IntentCountryScopeDto = {
    country: string;
    countryCode: number;
    cities: string[];
};

export type IntentSignalEventInfoDto = {
    topic: string;
    countriesScopes: readonly IntentCountryDto[];
    compositeScore: number;
    compositeScoreDelta: number;
};

type IntentSignalDomainEventInfoDto = {
    readonly domain: string;
    readonly events: readonly IntentSignalEventInfoDto[];
};

export type IntentSignalCompanyEventDto = BaseSignalEventDto & {
    readonly type: SignalType.Intent;
    readonly events: readonly IntentSignalEventInfoDto[];
};

export type IntentSignalDomainsEventDto = BaseSignalEventDto & {
    readonly type: SignalType.Intent;
    readonly domains: readonly IntentSignalDomainEventInfoDto[];
};

export type IntentSignalEventDto = IntentSignalCompanyEventDto | IntentSignalDomainsEventDto;

export type SignalEventDto =
    | NewsSignalEventDto
    | TechnologySignalEventDto
    | AdNetworkSignalEventDto
    | TrafficSignalEventDto
    | EcommerceSignalEventDto
    | IntentSignalEventDto;

export type SignalEventsDataDto = SignalEventDto[];

export type SignalsEventsGroupedByType = {
    [SignalType.News]: readonly NewsSignalEventDto[];
    [SignalType.Technology]: readonly TechnologySignalEventDto[];
    [SignalType.AdNetwork]: readonly AdNetworkSignalEventDto[];
    [SignalType.Traffic]: readonly TrafficSignalEventDto[];
    [SignalType.Ecommerce]: readonly EcommerceSignalEventDto[];
    [SignalType.Intent]: readonly IntentSignalEventDto[];
};

export type SignalEventWithLink = Extract<SignalEventDto, { url: string }>;

export const isSignalEventWithLink = (signalEvent: SignalEventDto): signalEvent is SignalEventWithLink => {
    return typeof (signalEvent as SignalEventWithLink).url !== "undefined";
};

export const isNewsSignalEvent = (signalEvent: Readonly<SignalEventDto>): signalEvent is NewsSignalEventDto => {
    return signalEvent.type === SignalType.News;
};

export const isTrafficSignalEvent = (signalEvent: Readonly<SignalEventDto>): signalEvent is TrafficSignalEventDto => {
    return signalEvent.type === SignalType.Traffic;
};

export const isIntentSignalCompanyEvent = (
    signalEvent: Readonly<SignalEventDto>,
): signalEvent is IntentSignalCompanyEventDto => {
    return signalEvent.type === SignalType.Intent && "events" in signalEvent;
};

export const isIntentSignalDomainsEvent = (
    signalEvent: Readonly<SignalEventDto>,
): signalEvent is IntentSignalDomainsEventDto => {
    return signalEvent.type === SignalType.Intent && "domains" in signalEvent;
};
